<script setup lang="ts">
import { computed } from 'vue'
import { DEFAULTDBTAB, IDECONSTANT } from '@/utils/ide'
import { useIdeStore } from '@/stores/ide.store'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import utilModelsService from '@/services/util.models.service'
import projectTreeService from '@/services/ide/projectTree.service'
import editorService from '@/services/ide/editor.service'
import advanceCompilerDatabaseService from '@/services/ide/advanceCompilerDatabase.service'
// import closeIcon from '@/assets/images/ide/Close.svg' // Remove 🗑️

const ideStore = useIdeStore()
const advCompDbStore = useAdvanceCompilerDatabaseStore()

const activeTabItems = computed(() => {
  return ideStore.uniqueActiveItemAdvIde || []
})

const activeItemName = computed(() => {
  const activeItem = ideStore.activeItem
  if (activeItem?.parent === '/') {
    return activeItem?.name
  } else {
    return activeItem?.name
  }
})

const isDataBaseView = computed(() => {
  return ideStore.isAdvancedCompilerDatabase
})

const isDefaultTabPresent = computed(() => {
  const index = ideStore.uniqueActiveItemAdvIde.findIndex((item) => item?.id === null)
  return index !== -1
})

/**
 * @description close active tab
 * @param item name of icon to close
 */
const closeActiveTab = (item: string) => {
  ideStore.removeUniqueActiveItemAdvIde(item)
  const nextActiveItem = activeItemName.value?.length && activeTabItems.value[0]
  if (!isDataBaseView.value) {
    projectTreeService.setActiveItem(nextActiveItem)
  } else {
    advanceCompilerDatabaseService.setActiveTabAndEditorContent(nextActiveItem)
  }
  editorService.heightChangeFunction(IDECONSTANT.CODE_EDITOR)
}

/**
 * @description format editable types
 * @param types types to format
 * @returns formatted types
 */
const formatEditableTypes = (types: string[]) => {
  const parsedTypes = JSON.parse(JSON.stringify(types))
  // if not last item add a space
  return parsedTypes.reduce((acc: string, type: string, index: number) => {
    if (index === parsedTypes.length - 1) {
      return `${acc} ${type}`
    }
    return `${acc} ${type},`
  }, '')
}
/**
 * @description select active item
 * @param name name of icon to open ide
 */
const selectActiveItem = (name: string) => {
  if (ideStore.editingInProcess) return
  const item = name || {}
  if (!isDataBaseView.value) {
    const editableTypes = ideStore.ideMeta?.editableTypes || []
    let editable: boolean = false

    for (const type of editableTypes) {
      // @ts-ignore
      if (item?.name.endsWith(type)) {
        editable = true
        break
      }
    }

    if (!editable) {
      utilModelsService.alertTimeOut(
        `Only ${formatEditableTypes(editableTypes)} file types can be edited`,
        'For other file types, download, edit locally and upload.'
      )
      return
    }
    projectTreeService.setActiveItem(item)
  } else {
    advanceCompilerDatabaseService.setActiveTabAndEditorContent(item)
  }
  editorService.heightChangeFunction(IDECONSTANT.CODE_EDITOR)
}

/**
 * Open new query tab to create new query file
 */
const handleClickNewQuery = () => {
  if (!isDefaultTabPresent.value) {
    ideStore.setUniqueActiveItemAdvIde({
      name: DEFAULTDBTAB,
      id: null
    })
  }
  ideStore.setActiveFileDataBase({ id: null })
  editorService.setEditorSession(IDECONSTANT.CODE_EDITOR, '')
}

/**
 * @param item tab details
 * @returns boolean info of tab is actives
 */
const isActiveTab = (item: any) => {
  if (isDataBaseView.value) {
    return ideStore.activeItem.id === item?.id
  } else {
    return activeItemName.value == item?.name
  }
}
</script>

<template>
  <div class="flex h-full w-full cursor-pointer flex-row">
    <div
      class="flex h-full w-full"
      :class="{ 'pointer-events-none': isDataBaseView && advCompDbStore.isCodeExecutingDb }"
    >
      <div
        class="stroke-primary-default flex min-w-32 flex-row border-r px-1 py-1.5"
        :class="[
          {
            'background-primary': isActiveTab(item),
            'border-b ': !isActiveTab(item)
          }
        ]"
        v-for="(item, index) in activeTabItems"
        :key="index"
      >
        <div class="flex w-full flex-row items-center justify-evenly">
          <button @click="selectActiveItem(item)" class="start-0 flex justify-center text-sm">
            <span>{{ item?.name }}</span>
          </button>
          <button
            v-if="activeTabItems.length > 1"
            @click="closeActiveTab(item)"
            class="flex cursor-pointer items-center justify-center"
          >
            <FontAwesomeIcon icon="fa-xmark" class="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="isDataBaseView && !isDefaultTabPresent"
      class="mx-2.5 my-auto"
      :class="{ 'pointer-events-none': isDataBaseView && advCompDbStore.isCodeExecutingDb }"
    >
      <FontAwesomeIcon
        icon="fa-plus"
        class="color-secondary-700 dark:color-neutral-300"
        @click="handleClickNewQuery"
      />
    </div>
  </div>
</template>
